/**
 * The stateful half of manual job attribution (ENG-2440): search state, the
 * debounce ahead of the request, and the match submission. Mounted only while
 * the dialog is open — see `manual-job-match.tsx` — so each open starts from an
 * empty search rather than the last one's leftovers.
 */
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import { DialogFooter } from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import type { useMatchDocumentJob } from "@/accounts-payable/query";
import { useSearchJobs } from "@/accounts-payable/query";
import type {
  ApDocumentType,
  ApJobSearchResult,
} from "@/accounts-payable/types";
import {
  documentTypeLabel,
  isJobSearchRateLimited,
} from "@/accounts-payable/utils";
import { getErrorMessage } from "@/core/api/errors";
import { useDebounce } from "@/core/hooks";
import { JobSearchCombobox } from "./job-search-combobox";

/**
 * Debounce ahead of the search request — one request per pause, not per key.
 * Each request costs a fan-out of ServiceTitan calls against a per-tenant rate
 * limit shared with background sync and payroll, so this leans slow.
 */
const SEARCH_DEBOUNCE_MS = 500;

export const ManualJobMatchForm = ({
  businessId,
  documentId,
  documentType = "invoice",
  agentRunId,
  matchJob,
  onClose,
}: {
  businessId: string | null;
  documentId: string;
  documentType?: ApDocumentType;
  agentRunId: string | null;
  /** Owned by the button above, so dismissing the dialog can't orphan it. */
  matchJob: ReturnType<typeof useMatchDocumentJob>;
  onClose: () => void;
}) => {
  const kind = documentTypeLabel(documentType);
  const [query, setQuery] = useState("");
  const [selectedJob, setSelectedJob] = useState<ApJobSearchResult | null>(
    null,
  );
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);

  /**
   * A selection belongs to the results it was picked out of. Editing the
   * search text keeps the old job selected and the confirm button live, so
   * confirming mid-search could attribute a job that the current results
   * don't even list. Trimmed, so trailing whitespace isn't a "new" search.
   */
  const onQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim() !== query.trim()) setSelectedJob(null);
  };
  const {
    data: jobs = [],
    error,
    isLoading,
    isFetching,
    isError,
    isPlaceholderData,
  } = useSearchJobs(businessId, debouncedQuery);

  /**
   * Keystrokes still waiting on the debounce count as loading: the results on
   * screen answer `debouncedQuery`, so without this the gap between the two
   * renders a premature "No jobs match that search." Placeholder data counts
   * too — those are the *previous* query's jobs, kept only to avoid a flash,
   * and selecting one would match the wrong job.
   */
  const isSearchPending =
    isLoading || isPlaceholderData || query !== debouncedQuery;

  const confirm = () => {
    if (!selectedJob) return;
    matchJob.mutate(
      { documentId, job: selectedJob },
      {
        onSuccess: (document) => {
          const agentStarted =
            document.agent_run_id !== null &&
            document.agent_run_id !== agentRunId;
          toast.success(
            agentStarted
              ? `${kind.capitalized} matched to job ${selectedJob.job_number} — the agent is running against the new match.`
              : `${kind.capitalized} matched to job ${selectedJob.job_number}.`,
          );
          onClose();
        },
        onError: (error: unknown) =>
          toast.error(`Couldn’t match the ${kind.lower} to that job`, {
            description: getErrorMessage(error),
          }),
      },
    );
  };

  return (
    <>
      <Stack gap="md" fullWidth className="px-6">
        <Typography variant="bodyW2" textColor="tertiary">
          Pick the ServiceTitan job this {kind.lower} belongs to. The agent
          re-runs against the new match once it’s saved.
        </Typography>
        <JobSearchCombobox
          query={query}
          onQueryChange={onQueryChange}
          jobs={jobs}
          isLoading={isSearchPending}
          isFetching={isFetching}
          isError={isError}
          isRateLimited={isJobSearchRateLimited(error)}
          selectedJob={selectedJob}
          onSelect={setSelectedJob}
          disabled={matchJob.isPending}
        />
      </Stack>
      <DialogFooter>
        <Button
          variant="secondary"
          size="sm"
          onClick={onClose}
          disabled={matchJob.isPending}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={confirm}
          loading={matchJob.isPending}
          disabled={!selectedJob || matchJob.isPending}
        >
          {selectedJob
            ? `Match to job ${selectedJob.job_number}`
            : "Match to job"}
        </Button>
      </DialogFooter>
    </>
  );
};
